import { useState, useCallback } from 'react';
import { getCSRFToken } from '../utils/csrf';
import { dispatchCartCount } from '../utils/cartBadge';
import { route } from '../utils/route';
import { gtagAsync } from '../utils/tracking';
import { getTabId } from './useCartSync';

declare const dataLayer: unknown[];

interface UseAddToCartReturn {
  addToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export function useAddToCart(): UseAddToCartReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addToCart = useCallback(async (
    productId: number,
    formatId: number,
    quantity: number
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(route('api.cart.add_item'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
          'X-Tab-Id': getTabId(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: productId,
          format_id: formatId,
          quantity: quantity,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al agregar al carrito');
      }

      const data = await response.json();

      // GA4 e-commerce tracking (async to avoid blocking INP)
      gtagAsync('event', 'add_to_cart', {
        currency: 'ARS',
        items: [{
          item_id: productId,
          quantity: quantity,
        }],
      });

      // PostHog tracking (via GTM dataLayer)
      dataLayer.push({ ecommerce: null });
      dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'ARS',
          items: [{ item_id: productId, format_id: formatId, quantity }],
        },
      });

      // Update badges from response summary (no extra GET needed)
      if (data.summary?.count != null) {
        dispatchCartCount(data.summary.count);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Error desconocido';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    addToCart,
    loading,
    error,
  };
}
